import {
  useWSModal,
  WSActions,
  WSDivider,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useCreateRequirementDefinition } from "../../../../query/requirementDefinitions/mutation/useCreateRequirementDefinition";
import {
  DefinitionDataSourceType,
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useUserId } from "../../../../query/hooks/helpers";
import { useAddRequirementToEngagement } from "../../../../query/engagements/mutations/useAddRequirementToEngagement";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { RequirementReviewStrategy } from "@wingspanhq/payments/dist/interfaces/requirement";

interface ConfirmCreateRequirementModalProps {
  onClose: () => void;
  onSuccess: (requirement: IRequirementDefinitionResponse) => void;
  engagementId?: string;

  dataSourceType?: DefinitionDataSourceType;
  dataSourceId?: string;
  name: string;
  description?: string;
  requirementType: RequirementType;
  reviewStrategy?: RequirementReviewStrategy;
}

const ConfirmCreateRequirementModal: React.FC<
  ConfirmCreateRequirementModalProps
> = ({
  onClose,
  dataSourceType,
  dataSourceId,
  name,
  description,
  requirementType,
  engagementId,
  reviewStrategy,
  onSuccess
}) => {
  const userId = useUserId();
  const [createRequirement, createRequirementMeta] =
    useCreateRequirementDefinition();
  const [addRequirementToEngagement, addRequirementToEngagementMeta] =
    useAddRequirementToEngagement(engagementId as string);
  return (
    <WSList gap="XL">
      <WSInfoBox>
        <WSText.ParagraphXs weight="medium" mb="XS">
          Confirm requirement creation
        </WSText.ParagraphXs>

        <WSText.ParagraphXs weight="book">
          Once a requirement is created, it cannot be edited.
          <br />
          If the requirement needs to be changed, a new version must be created
          and configured.
          <br />
          <br />
          Existing requirements can be previewed, attached, or removed from
          engagement type at any time.
        </WSText.ParagraphXs>
      </WSInfoBox>

      <WSDivider />

      <WSErrorMessage
        error={
          createRequirementMeta.error || addRequirementToEngagementMeta.error
        }
        contextKey="Other"
      />

      <WSActions
        buttons={[
          {
            label: "Create requirement",
            kind: "Primary",
            name: "createRequirementDefinition",
            loading:
              createRequirementMeta.isLoading ||
              addRequirementToEngagementMeta.isLoading,
            onClick: async () => {
              await createRequirement(
                {
                  clientId: userId,
                  dataSourceType,
                  dataSourceId,
                  name,
                  description,
                  requirementType,
                  reviewStrategy
                },
                {
                  onSuccess: async (
                    requirement: IRequirementDefinitionResponse
                  ) => {
                    if (engagementId) {
                      // update engagement with new requirement
                      await addRequirementToEngagement(
                        {
                          requirementId: requirement.requirementDefinitionId
                        },
                        {
                          onSuccess: () => {
                            onSuccess(requirement);

                            onClose();
                          }
                        }
                      );
                    } else {
                      onSuccess(requirement);

                      onClose();
                    }
                  }
                }
              );
            }
          },
          {
            label: "Cancel",
            kind: "Secondary",
            onClick: onClose
          }
        ]}
        alignment="fill"
      />
    </WSList>
  );
};

export function useConfirmCreateRequirementModal() {
  return useWSModal(ConfirmCreateRequirementModal, {
    title: "Create requirement",
    size: "S"
  });
}
